import { useMutation } from "@tanstack/react-query";
import { SnackbarProvider, enqueueSnackbar } from "notistack";
import { useNavigate, useNavigation } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useAuth } from "~/contexts/AuthProvider";
import { usePageTitle } from "~/hooks/usePageTitle";
import { useTeams } from "~/hooks/useTeams";
import SelectTeamLayout from "~/pages/selectTeam/SelectTeamLayout";
import { routes } from "~/router";

import { Team, UserAttributes } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { Icon } from "../../assets/icons/Icon";
import Snackbar from "../../components/ui/snackbar/Snackbar";
import Spinner from "../../components/ui/spinner/Spinner";
import styles from "./SelectTeamPage.module.scss";

const SelectTeamPage = () => {
  usePageTitle(PAGE_TITLES.selectTeam);

  const navigate = useNavigate();
  const navigation = useNavigation();
  const isLoaderLoading = navigation.state === "loading";
  const { refetchUser } = useAuth();
  const { data: teams = [], isFetching: isTeamsLoading } = useTeams();

  const { mutate: updateUser, isPending: userUpdatePending } = useMutation({
    mutationFn: async (data: UserAttributes) => {
      const response = await apiClient.user.userPartialUpdate(data);

      return response.data;
    },
    onSuccess: async () => {
      await refetchUser();

      enqueueSnackbar("User settings updated successfully", {
        variant: "success",
      });
      navigate(routes.home());
    },
    onError: (error) => {
      // @ts-expect-error from the response
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, { variant: "error", persist: true });
    },
  });

  const handleTeamClick = (team: Team) => {
    if (!userUpdatePending) {
      //@ts-expect-error BE type
      updateUser({ current_team_id: team.id });
    }
  };
  const renderTeamItem = (team: Team) => {
    return (
      <div
        key={team.id}
        className={styles.teamItem}
        onClick={() => handleTeamClick(team)}
      >
        <div className={styles.teamItemTitle}>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.teamLogo}>
            {team.logo ? (
              <img
                src={team.logo.url}
                alt={team.name}
                className={styles.logoImage}
              />
            ) : (
              <div className={styles.logoPlaceholder}>
                {team.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.teamName}>{team.name}</div>
        </div>
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <Icon iconName="ArrowRightIcon" className={styles.arrowIcon} />
      </div>
    );
  };

  const isLoading = userUpdatePending || isLoaderLoading || isTeamsLoading;

  return (
    <SnackbarProvider
      maxSnack={9}
      Components={{
        error: Snackbar,
        success: Snackbar,
        warning: Snackbar,
        info: Snackbar,
        default: Snackbar,
      }}
      TransitionProps={{ direction: "left" }}
      anchorOrigin={{ horizontal: "right", vertical: "top" }}
      autoHideDuration={5000}
    >
      <SelectTeamLayout>
        {isLoading ? (
          <div className={styles.spinnerWrapper}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.wrapper}>
            <h1 className={styles.title}>Select team</h1>

            <div className={styles.formContainer}>
              <h3 className={styles.description}>
                Whoops! It looks like you're trying to access a restricted
                resource.
              </h3>

              {teams && teams.length > 0 ? (
                <>
                  <p className={styles.prompt}>
                    Please select a team to continue.
                  </p>

                  <div className={styles.teamList}>
                    {teams?.map((team) => renderTeamItem(team))}
                  </div>
                </>
              ) : (
                <>
                  <p>You don't have available teams.</p>
                </>
              )}
            </div>
          </div>
        )}
      </SelectTeamLayout>
    </SnackbarProvider>
  );
};

export default SelectTeamPage;
